import { Pipe, PipeTransform, inject } from '@angular/core';

import { LanguageService } from '@fcom/ui-translate';

/**
 * Pipe that joins an array of strings with a localized conjunction.
 *
 * @example
 * ```html
 * <p>{{ ['one', 'two', 'three'] | finIntlJoin }}</p>
 * ```
 * @external Intl.ListFormat
 * @param value - An array of strings to join.
 * @requires LanguageService
 * @returns A string with the joined values i.e. 'one, two and three' .
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/ListFormat MDN documentation} for more information.
 */
@Pipe({
  name: 'finIntlJoin',
  standalone: false,
})
export class IntlJoinPipe implements PipeTransform {
  private languageService = inject(LanguageService);

  private listFormat: Intl.ListFormat;

  private readonly listOptions: Intl.ListFormatOptions = {
    style: 'long',
    type: 'conjunction',
  };

  constructor() {
    this.listFormat = new Intl.ListFormat(
      this.languageService?.localeValue?.replace('_', '-') || 'en-GB',
      this.listOptions
    );
  }

  transform(value: string[]): string {
    if (!value || !Array.isArray(value) || (Boolean(value.length) && 'string' !== typeof value[0])) {
      throw new TypeError('Value for INTL join must be an array of strings.');
    }
    return this.listFormat.format(value);
  }
}
